class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.clampQuantitiesToLimit();
  }

  async clampQuantitiesToLimit() {
    if (this.quantityClampInProgress) return;
    const inputs = [...this.querySelectorAll('.quantity__input[data-line-key]')];
    const overLimitInputs = inputs.filter((input) => Number(input.value) > 50);
    if (overLimitInputs.length === 0) return;

    this.quantityClampInProgress = true;
    this.setQuantityClampLoading(true);
    const updates = {};
    const originalValues = new Map();
    overLimitInputs.forEach((input) => {
      originalValues.set(input, input.value);
      input.value = 50;
      updates[input.dataset.lineKey] = 50;
      input.closest('quantity-input')?.querySelector('[name="plus"]')?.setAttribute('disabled', 'disabled');
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      const parsedState = await response.json();
      if (!response.ok || parsedState.errors) throw new Error(parsedState.errors || 'Unable to limit cart quantity');

      this.getSectionsToRender().forEach((section) => {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = parsedState.sections?.[section.id];
        if (!target || !html) return;
        target.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
    } catch (error) {
      originalValues.forEach((value, input) => {
        if (input.isConnected) input.value = value;
      });
      console.error('Unable to limit cart quantities to 50.', error);
    } finally {
      this.setQuantityClampLoading(false);
      this.quantityClampInProgress = false;
    }
  }

  setQuantityClampLoading(isLoading) {
    const drawerInner = this.querySelector('.drawer__inner');
    const overlay = this.querySelector('.cart-drawer__loading-overlay');
    this.classList.toggle('is-updating', isLoading);
    drawerInner?.setAttribute('aria-busy', String(isLoading));
    if (overlay) {
      overlay.hidden = !isLoading;
      overlay.setAttribute('aria-hidden', String(!isLoading));
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
